import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  Card,
  CardMedia,
  CardActions,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import ImageUploadCropDialog from './ImageUploadCropDialog';
import {
  normalizeImagePath,
  toAdminImageUrl,
  validateImageFile,
} from '../utils/imageUploadCore';

interface ImageUploadProps {
  onImageUploaded: (imageUrl: string) => void;
  onImageRemoved?: () => void;
  currentImage?: string;
  label?: string;
  maxSize?: number; // en MB
  forcedAspectRatio?: number;
  defaultAspectRatio?: number;
  hideAspectRatioSelector?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageUploaded,
  onImageRemoved,
  currentImage,
  label = "Image",
  maxSize = 1,
  forcedAspectRatio,
  defaultAspectRatio,
  hideAspectRatioSelector = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [previewLoadFailed, setPreviewLoadFailed] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);

  const directImageUrl = currentImage
    ? toAdminImageUrl(normalizeImagePath(currentImage))
    : null;

  useEffect(() => {
    if (!currentImage) {
      if (objectUrlRef.current) {
        window.URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setImagePreviewUrl(null);
      setPreviewLoadFailed(false);
      return;
    }

    let cancelled = false;
    setPreviewLoadFailed(false);

    const loadImage = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const imageUrl = toAdminImageUrl(normalizeImagePath(currentImage));

        const response = await fetch(imageUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          if (!cancelled) {
            setImagePreviewUrl(null);
            setPreviewLoadFailed(true);
          }
          return;
        }

        const blob = await response.blob();
        if (cancelled) return;

        if (objectUrlRef.current) {
          window.URL.revokeObjectURL(objectUrlRef.current);
        }
        const url = window.URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setImagePreviewUrl(url);
        setPreviewLoadFailed(false);
      } catch {
        if (!cancelled) {
          setImagePreviewUrl(null);
          setPreviewLoadFailed(true);
        }
      }
    };

    void loadImage();

    return () => {
      cancelled = true;
    };
  }, [currentImage]);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      window.URL.revokeObjectURL(objectUrlRef.current);
    }
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    const validationError = validateImageFile(file, maxSize);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
    setEditorOpen(true);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploaded = (imageUrl: string) => {
    setUploading(true);
    setUploadProgress(100);
    onImageUploaded(imageUrl);
    setEditorOpen(false);
    setSelectedFile(null);
    setUploading(false);
    setTimeout(() => setUploadProgress(0), 1000);
  };

  const handleCropClose = () => {
    setEditorOpen(false);
    setSelectedFile(null);
  };

  const handleRemoveImage = () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      onImageRemoved?.();
    }
  };

  const displayImageUrl = imagePreviewUrl || directImageUrl;

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
      />

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700, fontSize: '0.95rem' }}>
        {label}
      </Typography>

      {currentImage && !uploading && (
        <Card sx={{ maxWidth: 400, mb: 2 }}>
          {displayImageUrl ? (
            <CardMedia
              component="img"
              height="200"
              image={displayImageUrl}
              alt={label}
              sx={{ objectFit: 'cover' }}
              onError={() => setPreviewLoadFailed(true)}
            />
          ) : (
            <Box
              sx={{
                height: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
              }}
            >
              <ImageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            </Box>
          )}
          {previewLoadFailed && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block' }}>
              Apercu indisponible, mais l&apos;image est enregistree.
            </Typography>
          )}
          <CardActions>
            <Button
              size="small"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Changer
            </Button>
            {onImageRemoved && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleRemoveImage}
              >
                Supprimer
              </Button>
            )}
          </CardActions>
        </Card>
      )}

      {!currentImage && !uploading && (
        <Paper
          sx={{
            p: 3,
            border: '2px dashed #ccc',
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Cliquez pour selectionner une image
          </Typography>
          <Typography variant="body2" color="text.secondary">
            JPEG, PNG, GIF, WEBP - Max {maxSize}MB
          </Typography>
        </Paper>
      )}

      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
            Upload en cours... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <ImageUploadCropDialog
        open={editorOpen}
        file={selectedFile}
        onClose={handleCropClose}
        onUploaded={handleUploaded}
        maxSize={maxSize}
        forcedAspectRatio={forcedAspectRatio}
        defaultAspectRatio={defaultAspectRatio}
        hideAspectRatioSelector={hideAspectRatioSelector}
      />
    </Box>
  );
};

export default ImageUpload;
